import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PACKAGE_CONFIGS } from "@/config/packages";
import { invalidatePackageConfigCache } from "@/lib/packageConfig";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2 } from "lucide-react";

// Admin CMS for package prices. Lets the sales lead edit price / priceLabel / waSave / eyebrow
// on any of the 11 packages without a redeploy. Reads defaults from the static config
// and overrides from MongoDB via /api/packages/overrides.
export default function PricingCMS() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const slugs = Object.keys(PACKAGE_CONFIGS);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/packages/overrides");
      setOverrides(data || {});
    } catch (err) {
      console.warn("pricing load failed", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const effective = (slug) => {
    const base = PACKAGE_CONFIGS[slug];
    const o = overrides[slug] || {};
    return {
      price: o.price ?? base.price,
      priceLabel: o.priceLabel ?? base.priceLabel,
      waSave: o.waSave ?? base.waSave,
      eyebrow: o.eyebrow ?? base.eyebrow,
    };
  };

  const save = async (slug, patch) => {
    setBusySlug(slug);
    try {
      await api.put(`/admin/package-overrides/${slug}`, patch);
      invalidatePackageConfigCache();
      await load();
      toast.success(`${PACKAGE_CONFIGS[slug].title} updated live`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusySlug(null);
    }
  };

  const reset = async (slug) => {
    setBusySlug(slug);
    try {
      await api.delete(`/admin/package-overrides/${slug}`);
      invalidatePackageConfigCache();
      await load();
      toast.success(`${PACKAGE_CONFIGS[slug].title} reverted to default`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusySlug(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-white/60 text-sm py-10" data-testid="pricing-cms-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading package prices…
      </div>
    );
  }

  return (
    <div data-testid="pricing-cms">
      <div className="mb-6">
        <h2 className="font-serif-vp text-3xl md:text-4xl">Package Pricing CMS</h2>
        <p className="text-white/60 mt-2 text-sm max-w-2xl">
          Edit prices, "save" badges, and seasonal eyebrows live — changes hit every
          landing page + <code className="text-[var(--vp-accent)]">/packages</code> grid
          in under 10 seconds. Leave a field blank to keep the default from code.
        </p>
      </div>
      <div className="grid gap-4">
        {slugs.map((slug) => (
          <PricingRow
            key={slug}
            slug={slug}
            base={PACKAGE_CONFIGS[slug]}
            current={effective(slug)}
            hasOverride={!!overrides[slug]}
            busy={busySlug === slug}
            onSave={(patch) => save(slug, patch)}
            onReset={() => reset(slug)}
          />
        ))}
      </div>
    </div>
  );
}

function PricingRow({ slug, base, current, hasOverride, busy, onSave, onReset }) {
  const [price, setPrice] = useState(current.price);
  const [priceLabel, setPriceLabel] = useState(current.priceLabel);
  const [waSave, setWaSave] = useState(current.waSave);
  const [eyebrow, setEyebrow] = useState(current.eyebrow);

  // Resync when upstream changes (e.g. after a save)
  useEffect(() => {
    setPrice(current.price);
    setPriceLabel(current.priceLabel);
    setWaSave(current.waSave);
    setEyebrow(current.eyebrow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.price, current.priceLabel, current.waSave, current.eyebrow]);

  const dirty =
    price !== current.price ||
    priceLabel !== current.priceLabel ||
    waSave !== current.waSave ||
    eyebrow !== current.eyebrow;

  const submit = () => {
    const patch = {};
    if (price !== base.price || price !== current.price) patch.price = Number(price);
    if (priceLabel !== base.priceLabel) patch.priceLabel = priceLabel;
    if (waSave !== base.waSave) patch.waSave = waSave;
    if (eyebrow !== base.eyebrow) patch.eyebrow = eyebrow;
    onSave(patch);
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[160px_1fr_auto] gap-4 items-center border border-white/10 rounded-lg p-4 bg-white/[0.02]"
      data-testid={`pricing-row-${slug}`}
    >
      <div>
        <div className="flex items-center gap-2">
          <span className="text-xs tracking-[0.18em] uppercase text-white/60">{slug}</span>
          {hasOverride && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--vp-accent)]/25 text-[var(--vp-accent)]" data-testid={`pricing-override-badge-${slug}`}>
              Override
            </span>
          )}
        </div>
        <div className="font-serif-vp text-lg mt-1">{base.title}</div>
        <div className="text-[11px] text-white/40 mt-0.5">{base.duration}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Price (₹)">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded text-sm focus:outline-none focus:border-[var(--vp-accent)]"
            data-testid={`pricing-input-price-${slug}`}
          />
        </Field>
        <Field label="WA Save Badge">
          <input
            value={waSave}
            onChange={(e) => setWaSave(e.target.value)}
            placeholder="₹8,000"
            className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded text-sm focus:outline-none focus:border-[var(--vp-accent)]"
            data-testid={`pricing-input-wasave-${slug}`}
          />
        </Field>
        <Field label="Price Label">
          <input
            value={priceLabel}
            onChange={(e) => setPriceLabel(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded text-sm focus:outline-none focus:border-[var(--vp-accent)]"
            data-testid={`pricing-input-label-${slug}`}
          />
        </Field>
        <Field label="Eyebrow / Season">
          <input
            value={eyebrow}
            onChange={(e) => setEyebrow(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded text-sm focus:outline-none focus:border-[var(--vp-accent)]"
            data-testid={`pricing-input-eyebrow-${slug}`}
          />
        </Field>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={submit}
          disabled={!dirty || busy}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[var(--vp-accent)] text-[var(--vp-primary)] text-xs font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`pricing-save-${slug}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
        {hasOverride && (
          <button
            onClick={onReset}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 hover:text-white text-xs tracking-wide"
            data-testid={`pricing-reset-${slug}`}
            title="Revert to code default"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.15em] uppercase text-white/50 block mb-1">{label}</span>
      {children}
    </label>
  );
}
